import React, { useEffect, useMemo } from "react";
import { isString, isUndefined } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { Checkbox, Label } from "../../../../../../Inputs";
import { Text } from "../../../../../../Inputs/react-hook-form";
import TextareaInput from "../../../../../../Inputs/Textarea";
import { SelectedSubmissionsBuilderField } from "../../reducer";
import {
  InputFieldPath,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import { SubmissionsBuilderFieldSettings } from "../../types";
import { FieldWrapper } from "../__styles__/SidebarColumn";
import { WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import {
  DefaultValuesSection,
  DisplaySettingsSection,
  FieldPermissionsConfig,
  FieldSettingConfig,
  TextDynamicDefaultInput,
} from "./shared";

const TextareaWidgetSettings = ({
  fieldPath,
  isModuleInput,
  readOnly,
}: SelectedSubmissionsBuilderField & SubmissionsBuilderFieldSettings) => {
  const { register, watch } =
    useFormContext<SubmissionsBuilderFormDataStructure>();
  const isFormulaField = !!watch(`${fieldPath}.formula`);

  return (
    <WidgetSettingsContainer>
      <FieldSettingConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isFormulaField={isFormulaField}
        readOnly={readOnly}
      />
      <div>
        <Label text={"Helper text"} htmlFor={`${fieldPath}.helperText`} />
        <Text {...register(`${fieldPath}.helperText`)} disabled={readOnly} />
      </div>
      <div>
        <Label text={"Placeholder text"} />
        <Text {...register(`${fieldPath}.placeholder`)} disabled={readOnly} />
      </div>
      {!isFormulaField && (
        <TextareaGenerativeAIPromptInput
          readOnly={readOnly}
          fieldPath={fieldPath}
        />
      )}

      <DefaultValuesSection showTooltip>
        <FieldWrapper data-testid="dynamic-default-value-wrapper">
          <TextDynamicDefaultInput fieldPath={fieldPath} readOnly={readOnly} />
        </FieldWrapper>
        <FieldWrapper data-testid="static-default-value-wrapper">
          <TextAreaStaticDefaultInput
            fieldPath={fieldPath}
            readOnly={readOnly}
          />
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} />
      <DisplaySettingsSection fieldPath={fieldPath} readOnly={readOnly} />
    </WidgetSettingsContainer>
  );
};

export const TextAreaStaticDefaultInput = ({
  fieldPath,
  readOnly,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
}) => {
  const { control, watch } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const defaultValue = watch(`${fieldPath}.defaultValue`);

  const value = useMemo<string | null>(() => {
    if (isString(defaultValue) || isUndefined(defaultValue)) {
      return defaultValue || "";
    }
    throw new Error(`defaultValue must be a string or undefined`);
  }, [defaultValue]);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.defaultValue`}
      render={({ field }) => {
        return (
          <TextareaInput
            value={value}
            label="Static default value"
            name={`${fieldPath}.defaultValue`}
            onChange={value => {
              // store no value (undefined) for ui:defaultValue instead of empty string in form state
              field.onChange(value || undefined);
            }}
            disabled={readOnly}
          />
        );
      }}
    />
  );
};

export const TextareaGenerativeAIPromptInput = ({
  fieldPath,
  readOnly,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
}) => {
  const { control, watch, setValue } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  // Watch the value of the checkbox
  const useGenerativeAI = watch(`${fieldPath}.useGenerativeAI`);
  const generativeAIPrompt = watch(`${fieldPath}.generativeAIPrompt`);
  const required = watch(`${fieldPath}.required`);

  useEffect(() => {
    if (required) {
      setValue(`${fieldPath}.useGenerativeAI`, false, { shouldValidate: true });
    }
  }, [required, fieldPath, setValue]);

  return (
    <div>
      <FieldWrapper>
        <Controller
          control={control}
          name={`${fieldPath}.useGenerativeAI`}
          defaultValue={!!generativeAIPrompt}
          render={({ field }) => {
            return (
              <Checkbox
                label="Use AI to complete field"
                value={useGenerativeAI}
                onChange={field.onChange}
                name={field.name}
                disabled={required || readOnly}
              />
            );
          }}
        />
      </FieldWrapper>

      <Controller
        control={control}
        name={`${fieldPath}.generativeAIPrompt`}
        render={({ field }) => {
          return (
            <TextareaInput
              value={generativeAIPrompt}
              label="AI prompt"
              name={field.name}
              onChange={field.onChange}
              disabled={!useGenerativeAI || readOnly}
            />
          );
        }}
      />
    </div>
  );
};

TextareaWidgetSettings.displayName = "TextareaWidgetSettings";

export default TextareaWidgetSettings;
